import { logger } from '../../../utils/logger'
import {
  setJsonSessionItem,
  getJsonSessionItem,
  SessionStorage,
} from '../../../utils/session-storage'
import type { ServiceHandler } from './service-registry'
import { getSessionStorageMetadataKey } from './setMetadata'
import type {
  SetMetadataItemRequest,
  SetMetadataItemResponse,
} from '@custom-embedded-code-sdk/methods/metadata'

/**
 * SET_METADATA_ITEM service handler
 *
 * Handles requests to set a single metadata item for the current campaign in session storage.
 * Sets the session storage key as `metadata_{campaignId}`
 */
export const setMetadataItem: ServiceHandler<
  SetMetadataItemRequest,
  SetMetadataItemResponse
> = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:setMetadataItem: No campaign ID available')
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    if (!payload || typeof payload !== 'object' || !('key' in payload) || !('value' in payload)) {
      sendErrorResponse(iframeId, messageId, 'Metadata item key and value are required')
      return
    }

    const storageKey = getSessionStorageMetadataKey(campaignId)
    const existingMetadata = getJsonSessionItem<SessionStorage>(storageKey) || {}
    const { key, value } = payload
    existingMetadata[key] = value
    setJsonSessionItem(storageKey, existingMetadata)

    logger('info', 'CustomEmbeddedCode:setMetadataItem: Successfully set metadata item', {
      context: { iframeId, messageId, campaignId, payload, storageKey },
    })

    sendSuccessResponse(iframeId, messageId, { success: true })
  } catch (error) {
    logger(
      'error',
      new Error('CustomEmbeddedCode:setMetadataItem: Failed to set metadata item', {
        cause: error,
      }),
      {
        context: { iframeId, payload, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to set metadata item')
  }
}
